import { useState } from 'react';

interface SearchAndFiltersProps {
  onSearch: (query: string) => void;
  onFilterChange: (filters: FilterOptions) => void;
}

interface FilterOptions {
  type: 'all' | 'badge' | 'level' | 'mission' | 'challenge';
  period: 'all' | 'today' | 'week' | 'month';
  sortBy: 'recent' | 'popular' | 'trending';
}

export function SearchAndFilters({ onSearch, onFilterChange }: SearchAndFiltersProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>({
    type: 'all',
    period: 'all',
    sortBy: 'recent'
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    onSearch(query);
  };

  const handleFilterChange = (key: keyof FilterOptions, value: string) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const activeFiltersCount = Object.values(filters).filter(v => v !== 'all' && v !== 'recent').length;

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <i className="ri-search-line text-xl text-gray-400"></i>
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Buscar por usuário, conquista ou #hashtag..."
          className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
        />
        {searchQuery && (
          <button
            onClick={() => {
              setSearchQuery('');
              onSearch('');
            }}
            className="absolute inset-y-0 right-0 pr-4 flex items-center"
          >
            <i className="ri-close-line text-xl text-gray-400 hover:text-gray-600"></i>
          </button>
        )}
      </div>

      {/* Filter Toggle Button */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
        >
          <i className="ri-filter-3-line text-lg text-gray-700"></i>
          <span className="text-sm font-medium text-gray-700">Filtros</span>
          {activeFiltersCount > 0 && (
            <span className="bg-teal-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
              {activeFiltersCount}
            </span>
          )}
        </button>

        {/* Quick Sort */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleFilterChange('sortBy', 'recent')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filters.sortBy === 'recent'
                ? 'bg-teal-100 text-teal-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <i className="ri-time-line mr-1"></i>
            Recentes
          </button>
          <button
            onClick={() => handleFilterChange('sortBy', 'popular')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filters.sortBy === 'popular'
                ? 'bg-teal-100 text-teal-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <i className="ri-fire-line mr-1"></i>
            Populares
          </button>
          <button
            onClick={() => handleFilterChange('sortBy', 'trending')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filters.sortBy === 'trending'
                ? 'bg-teal-100 text-teal-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <i className="ri-rocket-line mr-1"></i>
            Em alta
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-6 animate-in fade-in slide-in-from-top-2 duration-200">
          {/* Type Filter */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">
              Tipo de Conquista
            </label>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'all', label: 'Todas', icon: 'ri-apps-line' },
                { value: 'badge', label: 'Distintivos', icon: 'ri-medal-line' },
                { value: 'level', label: 'Níveis', icon: 'ri-bar-chart-line' },
                { value: 'mission', label: 'Missões', icon: 'ri-flag-line' },
                { value: 'challenge', label: 'Desafios', icon: 'ri-trophy-line' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => handleFilterChange('type', option.value)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    filters.type === option.value
                      ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <i className={`${option.icon} text-lg`}></i>
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Period Filter */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">
              Período
            </label>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'all', label: 'Todo período', icon: 'ri-calendar-line' },
                { value: 'today', label: 'Hoje', icon: 'ri-calendar-today-line' },
                { value: 'week', label: 'Esta semana', icon: 'ri-calendar-week-line' },
                { value: 'month', label: 'Este mês', icon: 'ri-calendar-month-line' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => handleFilterChange('period', option.value)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    filters.period === option.value
                      ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <i className={`${option.icon} text-lg`}></i>
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Clear Filters */}
          {activeFiltersCount > 0 && (
            <button
              onClick={() => {
                setFilters({ type: 'all', period: 'all', sortBy: 'recent' });
                onFilterChange({ type: 'all', period: 'all', sortBy: 'recent' });
              }}
              className="w-full py-2 text-sm text-teal-600 hover:text-teal-700 font-medium"
            >
              Limpar todos os filtros
            </button>
          )}
        </div>
      )}

      {/* Active Filters Tags */}
      {activeFiltersCount > 0 && (
        <div className="flex flex-wrap gap-2">
          {filters.type !== 'all' && (
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-medium">
              Tipo: {filters.type}
              <button
                onClick={() => handleFilterChange('type', 'all')}
                className="hover:bg-teal-200 rounded-full p-0.5"
              >
                <i className="ri-close-line"></i>
              </button>
            </span>
          )}
          {filters.period !== 'all' && (
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm font-medium">
              Período: {filters.period}
              <button
                onClick={() => handleFilterChange('period', 'all')}
                className="hover:bg-cyan-200 rounded-full p-0.5"
              >
                <i className="ri-close-line"></i>
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
